import { Typography } from "@kelp-dao/kernel-design-system";
import BaseImage from "@components/BaseImage";
import UTMLink from "@components/UTMLink";
import { DAPP_TO_VAULT, VAULT_INSURED_TAG_CLICKED } from "@constants/analytics";
import { trackEvent } from "utils";
import { ReactNode } from "react";
import Insured from "./insured";

type VaultHeaderProps = {
  title: string;
  insuredLink?: string;
  isLoadingAPYData: boolean;
  totalStakedEth: string;
  totalStaked: string;
  isLoadingTotalStaked: boolean;
  totalStakedLabel: string;
  apyLabel: string;
  apyValue: string;
  apySubvalue?: string;
  renderRewards?: ReactNode;
  logo: {
    src: string;
    label: string;
  };
  tooltipComponent?: ReactNode;
  auditedUrl?: string;
};

const VaultHeader = ({
  title,
  isLoadingAPYData,
  totalStakedEth,
  totalStaked,
  isLoadingTotalStaked,
  totalStakedLabel,
  apyLabel,
  apyValue,
  apySubvalue,
  renderRewards,
  logo,
  tooltipComponent,
  insuredLink,
}: // auditedUrl,
VaultHeaderProps) => {
  return (
    <div className="w-full flex justify-between items-start tablet:items-center">
      {/* Left: Breadcrumb + Title */}
      <div className="flex-1 min-w-0">
        <div className="flex gap-1 items-center font-normal text-sm text-text-secondary mb-0">
          <UTMLink
            isExternal={false}
            href="/gain"
            onClick={() => {
              trackEvent(DAPP_TO_VAULT, {
                source: "vault_header_breadcrumb",
                vault_title: title,
              });
            }}
          >
            <Typography
              type="body"
              variant="medium"
              className="text-black font-medium"
            >
              All vaults
            </Typography>
          </UTMLink>
          <Typography
            type="body"
            variant="medium"
            className="text-text-secondary"
          >
            {`>`}
          </Typography>
          <Typography
            type="body"
            variant="medium"
            className="text-text-secondary"
          >
            {title}
          </Typography>
        </div>
        <div className="flex items-center gap-3 md:gap-4 flex-col md:flex-row justify-start md:justify-between">
          <div className=" w-full mt-6 md:mt-0 flex flex-row items-center md:items-left gap-1.5">
            <div className="w-[54px] md:h-[54px]">
              <BaseImage
                height={54}
                width={54}
                src={logo.src}
                alt={logo.label}
              />
            </div>
            <div className="flex flex-col md:flex-row gap-0 tablet:gap-4 items-center flex-wrap min-w-0 ">
              <Typography
                type="heading"
                variant="default"
                className="text-[24px] md:text-[28px] lg:text-[32px] font-medium leading-[38.4px] tablet:leading-[42.6px] lg:leading-[57.6px] tracking-tight whitespace-nowrap font-sans"
              >
                {title}
              </Typography>
              {insuredLink && (
                <UTMLink
                  isExternal={true}
                  href={insuredLink}
                  onClick={() => {
                    trackEvent(VAULT_INSURED_TAG_CLICKED);
                  }}
                >
                  <Insured />
                </UTMLink>
              )}

              {/* {auditedUrl && (
              <UTMLink isExternal={true} href={auditedUrl}>
                <div className="p-1 tablet:p-1.5 gap-1 flex items-center rounded-lg border border-[#259F76] bg-[#259F76] h-8 w-fit shrink-0">
                  <Typography
                    type="body"
                    variant="xsmall"
                    className="text-white font-semibold text-xs leading-[14.4px]"
                  >
                    Audited
                  </Typography>
                  <BaseImage
                    src="/assets/shield.svg"
                    height={16}
                    width={16}
                    alt="verified"
                  />
                </div>
              </UTMLink>
            )} */}
            </div>
          </div>
          {/* Right: Stats (TVL and APY with rewards tooltip) */}
          <div className="flex gap-10 mt-4 px-2 md:mt-0 md:px-0 items-start justify-between md:justify-end w-full md:w-auto">
            <VaultStats
              label={totalStakedLabel}
              value={totalStaked}
              subvalue={totalStakedEth}
              isLoading={isLoadingTotalStaked}
            />
            <VaultStats
              label={apyLabel}
              value={apyValue}
              subvalue={apySubvalue}
              isLoading={isLoadingAPYData}
              tooltipComponent={tooltipComponent}
              renderRewards={renderRewards}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const VaultStats = ({
  label,
  value,
  subvalue,
  isLoading,
  tooltipComponent,
  renderRewards,
}: {
  label: string;
  value: string;
  subvalue?: string;
  isLoading: boolean;
  tooltipComponent?: ReactNode;
  renderRewards?: ReactNode;
}) => {
  return isLoading ? (
    <div className="h-5 w-[120px] bg-gray-200 rounded animate-pulse" />
  ) : (
    <div className="flex flex-col gap-1 md:gap-0.5 items-start md:items-end">
      <Typography
        type="misc"
        variant="overline"
        className="text-xs font-normal leading-[14.4px] text-text-secondary text-nowrap "
      >
        {label}
      </Typography>
      <div className="flex gap-1 items-center justify-start md:justify-end w-full ">
        <Typography
          type="heading"
          variant="small"
          className="text-[20px] md:text-[24px] lg:text-[28px] font-medium leading-[26.4px] tablet:leading-[38.4px] lg:leading-[42.6px] tracking-tight inline font-sans"
        >
          {value}
        </Typography>
        {tooltipComponent && <div className="mt-1">{tooltipComponent}</div>}
      </div>
      {renderRewards && renderRewards}
      {subvalue ? (
        <Typography
          type="body"
          variant="xsmall"
          className="text-xs font-semibold leading-[14.4px] text-text-secondary text-nowrap"
        >
          {subvalue}
        </Typography>
      ) : (
        <div className="h-[14px]" />
      )}
    </div>
  );
};

export default VaultHeader;
